import { type TFunction } from 'i18next'
import { atomWithReset } from 'jotai/utils'

import { type TranslationSchema } from '~/locales/resources'
import { type SurveyFilterGroups } from '~/utils'

// The four fixed analysis roles the wizard always offers (seeded with empty
// assignments), in display order. Single source of truth from which
// BuiltinQuestionRole derives, and which drives the builtin-role display loops
// in SurveyContextStep and ConfirmationStep. Distinct from user-added custom
// roles.
export const BUILTIN_QUESTION_ROLES = [
  'npiDriver',
  'importance',
  'trigger',
  'resistance',
] as const
type BuiltinQuestionRole = (typeof BUILTIN_QUESTION_ROLES)[number]

// The ten custom role keys in canonical order. Single source of truth for
// allocating the next custom key, for the add-button cap, and from which
// CustomQuestionRole derives. A custom role becomes "active" only when its key
// is present in questionAssignments (see activeCustomRoles).
export const CUSTOM_QUESTION_ROLES = [
  'custom1',
  'custom2',
  'custom3',
  'custom4',
  'custom5',
  'custom6',
  'custom7',
  'custom8',
  'custom9',
  'custom10',
] as const
type CustomQuestionRole = (typeof CUSTOM_QUESTION_ROLES)[number]

// What a custom slot is analysed BY. Builtin roles are always 'question'.
type CustomRoleAxis = 'customerAttribute' | 'question'

// Any question role: a fixed builtin role or a user-added custom role. This is
// the single key space for question assignments — builtin and custom roles are
// no longer modelled as separate state.
type QuestionRole = BuiltinQuestionRole | CustomQuestionRole

// role -> selected ids (one id per role in practice). Builtin keys are always present
// (seeded with []); custom keys are present ONLY while active.
// For a customer-attribute-axis custom role the single id is a customer attribute GROUP
// id, not a questionId — sharing this map keeps the wizard's "exactly one id per active
// slot" validation working for BOTH axes unchanged.
type QuestionAssignments = Partial<Record<QuestionRole, string[]>>

// custom role -> user-entered label TEXT. Key-set is kept in sync with the
// custom keys of questionAssignments by the wizard mutators.
type CustomLabels = Partial<Record<CustomQuestionRole, string>>

// custom role -> axis. Key-set is kept in sync with the custom keys of questionAssignments
// by the wizard mutators; an absent key reads as 'question'.
type CustomRoleAxes = Partial<Record<CustomQuestionRole, CustomRoleAxis>>

type FilterPatternDraft = {
  filterGroups: SurveyFilterGroups
  id: string
  name: string
}

type Phase =
  | 'commenting'
  | 'complete'
  | 'documenting'
  | 'input'
  | 'rendering'
  | 'tabulating'

// awaitId of the in-flight tabulation request, or null when none is running.
export const awaitIdAtom = atomWithReset<string | null>(null)
// Per-role "limit to brand-matching items" checkbox state, keyed by role/custom
// key. Absent key means the default (true); see useBasicOutputWizard.getBrandAligned.
export const brandAlignedAtom = atomWithReset<Record<string, boolean>>({})
// Selected brand group id, or null until the user picks one.
export const brandGroupIdAtom = atomWithReset<number | null>(null)
// Labels for active custom roles. The per-role questionIds live in
// questionAssignments (custom keys), so this atom holds label TEXT only.
export const customLabelsAtom = atomWithReset<CustomLabels>({})
// The selected id itself lives in questionAssignments (custom keys), so this atom holds
// the axis TAG only.
export const customRoleAxesAtom = atomWithReset<CustomRoleAxes>({})
// Whether the brand-group-wide outputs (9segs顧客分布 + カスタマイズ分析) are targeted.
// Independent of selectedBrandIds — the wizard requires at least one of the two,
// but both may be true at once (e.g. ブランドグループ全体 + individual brands).
export const isEntireBrandGroupSelectedAtom = atomWithReset<boolean>(false)
// Brand ids flagged as key competitors (for the AI prompt context).
export const keyCompetitorBrandIdsAtom = atomWithReset<number[]>([])
// Brand ids flagged as the user's own brands (for the AI prompt context).
export const ownBrandIdsAtom = atomWithReset<number[]>([])
// Brand ids chosen as the standard-analysis target set; the wizard requires
// isEntireBrandGroupSelected OR (hasAnyQuestionSelected AND this non-empty)
// before generation (see useBasicOutputWizard's isBrandSelectionValid).
export const selectedBrandIdsAtom = atomWithReset<number[]>([])
// Draft filter patterns; seeded with a single all-data 'ALL' pattern.
export const filterPatternDraftsAtom = atomWithReset<FilterPatternDraft[]>([
  { filterGroups: [], id: 'all', name: 'ALL' },
])
// Current generation phase driving the wizard's progress UI.
export const phaseAtom = atomWithReset<Phase>('input')
// Question assignment map (role -> questionIds). Builtin keys seeded empty;
// custom keys added/removed by the wizard. Key presence of a custom key marks
// that custom role active.
export const questionAssignmentsAtom = atomWithReset<QuestionAssignments>({
  importance: [],
  npiDriver: [],
  resistance: [],
  trigger: [],
})

// i18n keys for each builtin role's display label. Resolved via t() at the call
// site (matches the page's existing i18n convention).
export const questionRoleLabels = {
  importance: 'basic_output_role_importance_label',
  npiDriver: 'basic_output_role_npi_driver_label',
  resistance: 'basic_output_role_resistance_label',
  trigger: 'basic_output_role_trigger_label',
} as const satisfies Record<BuiltinQuestionRole, keyof TranslationSchema>

// i18n keys for each builtin role's help-tooltip description. Resolved via t().
export const questionRoleDescriptions = {
  importance: 'basic_output_role_importance_description',
  npiDriver: 'basic_output_role_npi_driver_description',
  resistance: 'basic_output_role_resistance_description',
  trigger: 'basic_output_role_trigger_description',
} as const satisfies Record<BuiltinQuestionRole, keyof TranslationSchema>

// i18n keys for each generated output type's display label. Keyed by the
// analyzer's `outputType` vocabulary: `customerAnalysis`,
// `standardAnalysis_<role>`, and `customizedAnalysis_<role>` (customized is
// emitted only for npiDriver / importance). Resolved via t() in
// resolveOutputTypeLabel.
export const outputTypeLabels = {
  customerAnalysis: 'basic_output_output_type_customer_analysis',
  customizedAnalysis_importance:
    'basic_output_output_type_customized_importance',
  customizedAnalysis_npiDriver:
    'basic_output_output_type_customized_npi_driver',
  standardAnalysis_importance: 'basic_output_output_type_standard_importance',
  standardAnalysis_npiDriver: 'basic_output_output_type_standard_npi_driver',
  standardAnalysis_resistance: 'basic_output_output_type_standard_resistance',
  standardAnalysis_trigger: 'basic_output_output_type_standard_trigger',
} as const satisfies Record<string, keyof TranslationSchema>

// Canonical section order for assembled report documents, keyed by the
// analyzer `outputType` vocabulary (customerAnalysis / standardAnalysis_<role>
// / customizedAnalysis_<role>).
export const canonicalOutputOrder: string[] = [
  'customerAnalysis',
  'standardAnalysis_npiDriver',
  'standardAnalysis_importance',
  'standardAnalysis_trigger',
  'standardAnalysis_resistance',
  'customizedAnalysis_npiDriver',
  'customizedAnalysis_importance',
]

// True when `role` is one of the ten custom role keys.
export const isCustomQuestionRole = (
  role: string,
): role is CustomQuestionRole =>
  (CUSTOM_QUESTION_ROLES as readonly string[]).includes(role)

// Custom roles currently active, in INSERTION order (the order keys were added
// to questionAssignments). Mirrors the old CustomQuestion[] array order and
// keeps display / planRenderTasks / document order mutually consistent.
export const activeCustomRoles = (
  assignments: QuestionAssignments,
): CustomQuestionRole[] => Object.keys(assignments).filter(isCustomQuestionRole)

// A customer-attribute-axis slot holds one GROUP id; both the submit payload and the render
// plan need that group's attribute ids. Shared so the two cannot diverge — they must describe
// the same outputs (see the outputKey note in planRenderTasks).
//
// The slot can only hold a group the selector offered, so an unresolvable id is a real bug:
// throw rather than sending an empty selection the server would reject anyway.
export const expandCustomerAttributeGroupId = (
  customerAttributeGroups: ReadonlyArray<{
    customerAttributes: ReadonlyArray<{ id: string }>
    id: string
  }>,
  groupId: string,
  role: string,
): string[] => {
  const group = customerAttributeGroups.find(({ id }) => id === groupId)
  if (!group) {
    throw new Error(
      `basic-output: customer attribute group ${groupId} not found for role ${role}`,
    )
  }

  return group.customerAttributes.map(({ id }) => id)
}

// True when `s` is one of the static outputTypeLabels keys. Uses an own-property
// check (NOT bare `in`) so prototype keys like 'toString' do not match and send
// undefined to t().
const isOutputTypeLabelKey = (s: string): s is keyof typeof outputTypeLabels =>
  Object.prototype.hasOwnProperty.call(outputTypeLabels, s)

// Resolve a human-readable label for an output type. Falls back to the
// user-defined custom-question label when the role is a custom key (no static
// entry above), and finally to the raw outputType when nothing matches.
export const resolveOutputTypeLabel = (
  outputType: string,
  customLabels: CustomLabels,
  t: TFunction,
): string => {
  if (isOutputTypeLabelKey(outputType)) {
    return t(outputTypeLabels[outputType])
  }

  if (outputType.startsWith('standardAnalysis_')) {
    const baseType = outputType.slice('standardAnalysis_'.length)
    if (isCustomQuestionRole(baseType)) {
      return customLabels[baseType] || baseType
    }
  }

  if (outputType.startsWith('customizedAnalysis_')) {
    const baseType = outputType.slice('customizedAnalysis_'.length)
    if (isCustomQuestionRole(baseType)) {
      return t('basic_output_output_type_customized_prefix', {
        label: customLabels[baseType] || baseType,
      })
    }
  }

  return outputType
}

export const isActiveGenerationPhase = (phase: Phase): boolean =>
  phase === 'commenting' ||
  phase === 'documenting' ||
  phase === 'rendering' ||
  phase === 'tabulating'

export type {
  BuiltinQuestionRole,
  CustomLabels,
  CustomQuestionRole,
  CustomRoleAxes,
  CustomRoleAxis,
  FilterPatternDraft,
  Phase,
  QuestionAssignments,
  QuestionRole,
}
